<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            game-header(
                :time='getTime'
                :slug='type'
                :game-type='parameters && parameters.courseType'
                :amount-of-played-rounds='amountOfExamples'
                :amount-of-rounds='parameters.numOfExamples'
                :show-rounds='true'
                @soundStateChanged="headerKey = !headerKey"
                :key="headerKey"
                :is-homework="!!exerciseId"
            )
            game-preparation-counter(v-if='prepareForGame' :counter="counter")
            .scanner-game-container__interactive-place(v-if='!prepareForGame')
                scanner-table(
                    v-if='!gameFinished && !isNextExampleButtonShown'
                    :items-to-remember='itemsToRemember'
                    :all-items='allItems'
                    :memorized='memorized'
                    :shaked-items="shakedItems"
                    :window-width="windowWidth"
                    v-model='selectedItems'
                    @clearShakedItems="shakedItems = []"
                )
                .scanner__buttons-container(v-if='!gameFinished && !isNextExampleButtonShown')
                    cs-button.scanner__button(v-if='showMemorizedButton' color="blue" @click='startGame')
                        | {{ $i18n.t('couples.couples_memorized', {mode: $i18n.t('couples.couples_all')})}}
                    cs-button.scanner__button(
                        v-if='gameStarted && memorized'
                        :disabled='disableReplyButton'
                        color="blue"
                        @click='processReply'
                    )
                        | {{ replyButtonName }}
                fireworks(v-if='gameFinished && !isOlympiad && !prepareForGame' :is-mobile='isMobile')
                .scanner-game-container__result-container(
                    v-if='gameFinished && !isOlympiad || isNextExampleButtonShown'
                )
                    result-image.scanner-game-container__result-image(status="success")
                    result-buttons(
                        :exercise-id="exerciseId"
                        games-type="fastReading"
                        show-restart-button
                                    @restartGame="restartGame"
                    )
                        template(slot="mainButton")
                            cs-button(
                                v-if='isNextExampleButtonShown'
                                color="blue"
                                @click='getExample'
                                v-focus="true"
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
                        slot
        cs-loader(v-else)
</template>

<script>
    /*eslint-disable*/
    import { cloneDeep, shuffle } from 'lodash';
    import ScannerGameItemsApi from '../../../../api/games/scannerGameItems';
    import ScannerTable from './ScannerTable.vue';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';
    import GamePreparationCounter from '../../GamePreparationCounter.vue';
    import { mapState } from 'vuex';

    const DEFAULT_VOLUME = 0.6;

    function DEFAULT_DATA() {
        return {
            itemsToRemember: [],
            allItems: [],
            isReplyButtonShown: false,
            chainOfAttempts: [[]],
            amountOfExamples: 0,
            isNextExampleButtonShown: false,
            prepareForGame: true,
            isAttemptCorrect: false,
            memorized: false,
            selectedItems: [],
            hideGameHeader: false,
            shakedItems: [],
        };
    }

    export default {
        mixins: [playBoxGameMixin, olympiadAnswerTimerMixin],
        watch: {
            selectedItems: {
                handler(array) {
                    this.isReplyButtonShown = array.length === this.itemsToRemember.length;
                },
                deep: true,
            },
        },
        data() {
            return DEFAULT_DATA();
        },
        updated() {
            !this.isNeedToChangeDevice && !this.isFetched && this.fetchData();
        },
        computed: {
            ...mapState({
                amountOfColumns(state) {
                    return (
                        ((state.trainer.game?.slug === 'Scanner' || state.trainer.game?.slug === 'ScannerWithTime') &&
                            state.trainer.game?.tableSize) ||
                        this.battleGameParameters.parameters.default.tableSize
                    );
                },
            }),
            isNeedToChangeDevice() {
                return this.windowWidth < 576 && this.amountOfColumns > 5;
            },
            showMemorizedButton() {
                return !this.memorized && !this.parameters.withTime;
            },
            disableReplyButton() {
                return !this.isReplyButtonShown;
            },
            replyButtonName() {
                if (this.selectedItems.length < this.itemsToRemember.length) {
                    return this.$i18n.t('trainer.trainer_gamePlayBox_moreForReply');
                }
                if (this.selectedItems.length > this.itemsToRemember.length) {
                    return this.$i18n.t('trainer.trainer_gamePlayBox_lessForReply');
                }
                return this.$i18n.t('trainer.trainer_gamePlayBox_reply');
            },
            goodAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.good));
                });
                return count;
            },
            badAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.bad));
                });
                return count;
            },
        },
        methods: {
            fetchData() {
                this.isFetched = true;
                this.getExercise()
                    .then(this.getExample)
                    .catch(e => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            getExample() {
                if (this.isAttemptCorrect && !this.isOlympiad) {
                    this.chainOfAttempts.push([]);
                    this.sendStat();
                }
                this.isAttemptCorrect = false;
                let paramsForRequest = {
                    amountOfItems: Math.pow(this.parameters.tableSize, 2),
                    type: this.parameters.mode,
                };
                ScannerGameItemsApi.getItemsByRequest(paramsForRequest)
                    .then(items => {
                        this.setData(items);
                        if (!this.prepareForGame) {
                            !this.timer && this.setTimer();
                            if (this.parameters.withTime) {
                                setTimeout(this.startGame, this.parameters.showTime * 1000);
                            }
                        }
                    })
                    .then(this.prepareForGame && this.registerSounds())
                    .catch(err => this.showErrorMessage(err));
            },
            setData(items) {
                if (this.parameters.mode !== 'mixed') {
                    this.itemsToRemember = items.slice(0, this.parameters.numOfUnknowns);
                } else {
                    this.itemsToRemember = [];
                    let amount = Math.round(this.parameters.numOfUnknowns / 2);
                    for (let i = 0; i < amount; i++) {
                        this.itemsToRemember.push(items[i]);
                    }
                    for (let i = items.length - 1; i > items.length - 1 - this.parameters.numOfUnknowns + amount; i--) {
                        this.itemsToRemember.push(items[i]);
                    }
                }
                this.allItems = items;
                this.isLoading = false;
                this.gameStarted = true;
                this.isNextExampleButtonShown = false;
            },
            beforeStart() {
                this.prepareForGame = false;
                !this.timer && this.setTimer();
                if (this.parameters.withTime) {
                    setTimeout(this.startGame, this.parameters.showTime * 1000);
                }
            },
            refreshGameStatBeforeSend() {
                this.gameStat.chainOfAttempts = this.chainOfAttempts;
                this.gameStat.attempts = this.isAttemptCorrect
                    ? this.chainOfAttempts[this.amountOfExamples - 1].length
                    : this.chainOfAttempts[this.amountOfExamples].length;
                this.gameStat.answers = this.amountOfExamples;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                const total = this.goodAnswers + this.badAnswers;
                return total > 0 ? parseInt((this.goodAnswers / total) * 100) : 0;
            },
            startGame() {
                this.memorized = true;
                this.allItems = shuffle(this.allItems);
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
            },
            processReply() {
                this.stopAnswerTimer();
                let elemOfExampleAttempts = {
                    good: 0,
                    bad: 0,
                };
                this.selectedItems.forEach(item => {
                    if (!item.correct) {
                        if (this.itemsToRemember.includes(item)) {
                            elemOfExampleAttempts.good++;
                            item.correct = true;
                        } else {
                            elemOfExampleAttempts.bad++;
                            item.correct = false;
                        }
                    }
                });
                this.chainOfAttempts[this.amountOfExamples].push(elemOfExampleAttempts);
                if (this.selectedItems.filter(item => item.correct).length === this.itemsToRemember.length) {
                    this.hideGameHeader = true;
                    this.amountOfExamples++;
                    this.isAttemptCorrect = true;
                    if (
                        this.parameters.exampleComplication &&
                        this.parameters.numOfUnknowns < Math.pow(this.parameters.tableSize, 2) - 1 &&
                        this.chainOfAttempts[this.amountOfExamples - 1].length === 1
                    ) {
                        this.parameters.numOfUnknowns++;
                    }
                    if (this.parameters.numOfExamples === this.amountOfExamples) {
                        this.scannerDone();
                        return;
                    }
                    if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                        this.sendStat();
                    }
                    this.resetData();
                    if (this.isOlympiad) {
                        this.chainOfAttempts.push([]);
                        this.isNextExampleButtonShown = false;
                        this.getExample();
                    }
                    return;
                }
                if (this.isOlympiad) {
                    this.resetData();
                    this.amountOfExamples++;
                    this.chainOfAttempts.push([]);
                    this.isNextExampleButtonShown = false;
                    this.isAttemptCorrect = false;
                    if (this.parameters.numOfExamples === this.amountOfExamples) {
                        this.scannerDone();
                        return;
                    }
                    this.getExample();
                } else {
                    this.shakedItems = [];
                    this.selectedItems.forEach(item => {
                        if (!item.correct) {
                            this.shakedItems.push(item);
                        }
                    });
                    setTimeout(() => {
                        this.shakedItems = [];
                    }, 800);
                    this.soundInstance = this.$createjs.Sound.play('clickIncorrect');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                    this.selectedItems = this.selectedItems.filter(({ correct }) => correct);
                }
                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.isAttemptCorrect = false;
                    this.sendStat();
                }
            },
            resetData() {
                this.isNextExampleButtonShown = true;
                this.gameStarted = false;
                this.hideGameHeader = false;
                this.memorized = false;
                this.stopTimer();
                this.timer = null;
                this.selectedItems = [];
                if (this.answerTimer) {
                    clearInterval(this.answerTimer);
                    this.answerTimer = null;
                }
            },
            scannerDone() {
                if (!this.isOlympiad) {
                    this.soundInstance = this.$createjs.Sound.play('gameDone');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                }
                this.gameFinished = true;
                this.sendStat();
            },
            beforeGameRestart() {
                this.resetData();
                Object.assign(this, DEFAULT_DATA());
            },
        },
        components: {
            GamePreparationCounter,
            ScannerTable,
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .scanner-game-container__interactive-place {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        justify-content: center;
        flex-direction: column-reverse;
        @include media($xlg) {
            flex-direction: row;
        }
        .scanner__buttons-container {
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            margin-top: 20px;
            margin-bottom: 20px;
            @include media($xlg) {
                min-width: 350px;
                margin-left: 20px;
            }
        }
    }

    .scanner-game-container__result-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .scanner-game-container__result-image {
        @include stack-space($space-base);
        max-width: 450px;
        max-height: 450px;
        vertical-align: middle;
    }

    .scanner__button {
        @media screen and (max-width: 640px) {
            width: 100%;
        }
    }
</style>
